import { debounce, find, isEmpty, keyBy, map } from "lodash";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Button from "antd/lib/button";
import Checkbox from "antd/lib/checkbox";
import Tabs from "antd/lib/tabs";
import routeWithUserSession from "@/components/ApplicationArea/routeWithUserSession";
import DynamicComponent from "@/components/DynamicComponent";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Filters from "@/components/Filters";

import { Dashboard, normalizeDashboardTabAssignment } from "@/services/dashboard";
import recordEvent from "@/services/recordEvent";
import resizeObserver from "@/services/resizeObserver";
import routes from "@/services/routes";
import location from "@/services/location";
import url from "@/services/url";
import useImmutableCallback from "@/lib/hooks/useImmutableCallback";

import useDashboard from "./hooks/useDashboard";
import DashboardHeader from "./components/DashboardHeader";

import "./DashboardPage.less";

const { TabPane } = Tabs;
const DEFAULT_TAB_ID = "default";

function DashboardSettings({ dashboardConfiguration }) {
  const { dashboard, updateDashboard } = dashboardConfiguration;
  return (
    <div className="m-b-10 p-15 bg-white tiled">
      <Checkbox
        checked={!!dashboard.dashboard_filters_enabled}
        onChange={({ target }) => updateDashboard({ dashboard_filters_enabled: target.checked })}
        data-test="DashboardFiltersCheckbox">
        Use Dashboard Level Filters
      </Checkbox>
    </div>
  );
}

DashboardSettings.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function AddWidgetContainer({ dashboardConfiguration, className, ...props }) {
  const { showAddTextboxDialog, showAddWidgetDialog } = dashboardConfiguration;
  return (
    <div className={cx("add-widget-container", className)} {...props}>
      <h2>
        <i className="zmdi zmdi-widgets" aria-hidden="true" />
        <span className="hidden-xs hidden-sm">
          Widgets are individual query visualizations or text boxes you can place on your dashboard in various
          arrangements.
        </span>
      </h2>
      <div>
        <Button className="m-r-15" onClick={showAddTextboxDialog} data-test="AddTextboxButton">
          Add Textbox
        </Button>
        <Button type="primary" onClick={showAddWidgetDialog} data-test="AddWidgetButton">
          Add Widget
        </Button>
      </div>
    </div>
  );
}

AddWidgetContainer.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
};

function DashboardComponent(props) {
  const activeTabIdRef = useRef(DEFAULT_TAB_ID);
  const dashboardConfiguration = useDashboard(props.dashboard, { activeTabIdRef });
  const {
    dashboard,
    filters,
    setFilters,
    loadDashboard,
    loadWidget,
    removeWidget,
    saveDashboardLayout,
    globalParameters,
    updateDashboard,
    refreshDashboard,
    refreshWidget,
    editingLayout,
    setGridDisabled,
  } = dashboardConfiguration;
  const tabs = useMemo(() => dashboard.getTabsDefs(), [dashboard]);
  const hasTabbedLayout = dashboard.hasTabbedLayout();
  const [activeTabId, setActiveTabId] = useState(() => dashboard.getActiveTabId(location.search.tab));
  activeTabIdRef.current = activeTabId;
  const activeTabIndex = useMemo(() => tabs.findIndex(t => t.id === activeTabId), [tabs, activeTabId]);

  const [pageContainer, setPageContainer] = useState(null);
  const [bottomPanelStyles, setBottomPanelStyles] = useState({});
  const tabLayouts = dashboard.getTabLayouts();
  const activeTabLayout = tabLayouts[activeTabId] || {};
  const onParametersEdit = parameters => {
    const paramOrder = map(parameters, "name");
    updateDashboard({ options: { ...(dashboard.options || {}), globalParamOrder: paramOrder } });
  };
  const filteredWidgets = useMemo(() => dashboard.getWidgetsForTab(activeTabId, dashboard.widgets), [dashboard, activeTabId]);
  const tabParameters = useMemo(
    () => dashboard.getGlobalParametersForTab(activeTabId, globalParameters),
    [dashboard, activeTabId, globalParameters]
  );
  const parameterBars = useMemo(() => dashboard.getParameterBarsDefs(tabParameters), [dashboard, tabParameters]);
  const parameterAssignments = dashboard.options?.parameterBarAssignments || {};
  const parameterOrders = dashboard.options?.parameterBarOrders || {};
  const prevGlobalParamNamesRef = useRef(null);

  const handleWidgetTabAssign = useCallback(
    (widgetId, tabIds) => {
      const known = new Set(map(tabs, "id"));
      let cleaned = normalizeDashboardTabAssignment(tabIds).filter(id => known.has(id));
      if (!cleaned.length) {
        cleaned = [tabs[0]?.id || DEFAULT_TAB_ID];
      }
      updateDashboard({
        options: {
          ...(dashboard.options || {}),
          widgetTabAssignments: {
            ...(dashboard.options?.widgetTabAssignments || {}),
            [String(widgetId)]: cleaned,
          },
        },
      });
    },
    [dashboard.options, updateDashboard, tabs]
  );

  const handleParameterTabAssign = useCallback(
    (paramName, tabIds) => {
      const known = new Set(map(tabs, "id"));
      let cleaned = normalizeDashboardTabAssignment(tabIds).filter(id => known.has(id));
      if (!cleaned.length) {
        cleaned = [tabs[0]?.id || DEFAULT_TAB_ID];
      }
      updateDashboard({
        options: {
          ...(dashboard.options || {}),
          parameterTabAssignments: {
            ...(dashboard.options?.parameterTabAssignments || {}),
            [paramName]: cleaned,
          },
        },
      });
    },
    [dashboard.options, updateDashboard, tabs]
  );

  const assignmentTabs = useMemo(() => tabs.map(t => ({ id: t.id, name: t.name })), [tabs]);

  const widgetTabAssignmentOptions = useMemo(() => {
    if (!(editingLayout && hasTabbedLayout)) {
      return null;
    }
    return {
      tabs: assignmentTabs,
      assignments: dashboard.options?.widgetTabAssignments || {},
      fallbackTabId: tabs[0]?.id || DEFAULT_TAB_ID,
      onAssignmentChange: handleWidgetTabAssign,
    };
  }, [editingLayout, hasTabbedLayout, assignmentTabs, dashboard.options?.widgetTabAssignments, handleWidgetTabAssign, tabs]);

  const parameterTabAssignmentOptions = useMemo(() => {
    if (!(editingLayout && hasTabbedLayout)) {
      return null;
    }
    return {
      tabs: assignmentTabs,
      assignments: dashboard.options?.parameterTabAssignments || {},
      fallbackTabId: tabs[0]?.id || DEFAULT_TAB_ID,
      onAssignmentChange: handleParameterTabAssign,
    };
  }, [
    editingLayout,
    hasTabbedLayout,
    assignmentTabs,
    dashboard.options?.parameterTabAssignments,
    handleParameterTabAssign,
    tabs,
  ]);

  useEffect(() => {
    const names = globalParameters.map(p => p.name);
    const nameSet = new Set(names);

    if (!editingLayout || !hasTabbedLayout) {
      prevGlobalParamNamesRef.current = nameSet;
      return;
    }

    const prev = prevGlobalParamNamesRef.current;
    if (!(prev instanceof Set)) {
      prevGlobalParamNamesRef.current = nameSet;
      return;
    }

    const genuinelyNewNames = names.filter(n => !prev.has(n));
    prevGlobalParamNamesRef.current = nameSet;

    if (!genuinelyNewNames.length) {
      return;
    }

    const assigns = dashboard.options?.parameterTabAssignments || {};
    const patch = {};

    genuinelyNewNames.forEach(n => {
      if (normalizeDashboardTabAssignment(assigns[n]).length === 0) {
        patch[n] = [activeTabId];
      }
    });

    if (!Object.keys(patch).length) {
      return;
    }

    updateDashboard({
      options: {
        ...(dashboard.options || {}),
        parameterTabAssignments: {
          ...assigns,
          ...patch,
        },
      },
    });
  }, [editingLayout, hasTabbedLayout, globalParameters, activeTabId, dashboard.options, updateDashboard]);
  const layoutOverrides = useMemo(
    () =>
      filteredWidgets.reduce((acc, widget) => {
        const widgetId = String(widget.id);
        const saved = activeTabLayout[widgetId];
        if (saved) {
          acc[widgetId] = {
            ...widget.options.position,
            ...saved,
          };
        }
        return acc;
      }, {}),
    [filteredWidgets, activeTabLayout]
  );

  useEffect(() => {
    setActiveTabId(dashboard.getActiveTabId(location.search.tab));
  }, [dashboard]);

  const onTabChange = key => {
    const next = dashboard.getActiveTabId(key);
    setActiveTabId(next);
    // keep URL deep-link only for tabbed dashboards
    location.setSearch({ tab: hasTabbedLayout || tabs.length > 1 ? next : null }, true);
  };

  const withSoftInitOptions = useCallback(() => {
    if (hasTabbedLayout) {
      return dashboard.options || {};
    }

    const defaultTab = { id: DEFAULT_TAB_ID, name: "Overview", order: 0 };
    const widgetAssignments = dashboard.widgets.reduce((acc, widget) => {
      acc[String(widget.id)] = [DEFAULT_TAB_ID];
      return acc;
    }, {});
    const parameterAssignmentsInit = globalParameters.reduce((acc, parameter) => {
      acc[parameter.name] = [DEFAULT_TAB_ID];
      return acc;
    }, {});
    const defaultLayout = dashboard.widgets.reduce((acc, widget) => {
      acc[String(widget.id)] = { ...widget.options.position };
      return acc;
    }, {});

    return {
      ...(dashboard.options || {}),
      tabs: [defaultTab],
      activeTabIdDefault: DEFAULT_TAB_ID,
      widgetTabAssignments: widgetAssignments,
      parameterTabAssignments: parameterAssignmentsInit,
      tabLayouts: { [DEFAULT_TAB_ID]: defaultLayout },
    };
  }, [hasTabbedLayout, dashboard.options, dashboard.widgets, globalParameters]);

  const addTab = () => {
    const name = window.prompt("Tab name", "New Tab");
    if (!name || !name.trim()) {
      return;
    }
    const baseOptions = withSoftInitOptions();
    const baseTabs = hasTabbedLayout ? tabs : [{ id: DEFAULT_TAB_ID, name: "Overview", order: 0 }];
    const tabId = `tab-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;
    const nextTabs = [...baseTabs, { id: tabId, name: name.trim(), order: baseTabs.length }];
    const nextOptions = {
      ...baseOptions,
      tabs: nextTabs,
      tabLayouts: {
        ...(baseOptions.tabLayouts || {}),
        [tabId]: {},
      },
    };
    setActiveTabId(tabId);
    location.setSearch({ tab: tabId }, true);
    updateDashboard({ options: nextOptions });
  };

  const renameActiveTab = () => {
    if (!hasTabbedLayout) {
      return;
    }
    const activeTab = find(tabs, tab => tab.id === activeTabId);
    if (!activeTab) {
      return;
    }
    const nextName = window.prompt("Rename tab", activeTab.name);
    if (!nextName || !nextName.trim()) {
      return;
    }
    const nextTabs = tabs.map(tab => (tab.id === activeTab.id ? { ...tab, name: nextName.trim() } : tab));
    updateDashboard({ options: { ...(dashboard.options || {}), tabs: nextTabs } });
  };

  const removeActiveTab = () => {
    if (!hasTabbedLayout || tabs.length <= 1) {
      return;
    }
    const activeTab = find(tabs, tab => tab.id === activeTabId);
    if (!activeTab) {
      return;
    }
    if (!window.confirm(`Delete tab "${activeTab.name}"? Its widgets/parameters will move to another tab.`)) {
      return;
    }

    const remaining = tabs.filter(tab => tab.id !== activeTabId);
    const fallbackTabId = remaining[0].id;

    const nextWidgetAssignments = { ...(dashboard.options?.widgetTabAssignments || {}) };
    Object.keys(nextWidgetAssignments).forEach(widgetId => {
      let ids = normalizeDashboardTabAssignment(nextWidgetAssignments[widgetId]).filter(id => id !== activeTabId);
      if (!ids.length) {
        ids = [fallbackTabId];
      }
      nextWidgetAssignments[widgetId] = ids;
    });

    const nextParamAssignments = { ...(dashboard.options?.parameterTabAssignments || {}) };
    Object.keys(nextParamAssignments).forEach(paramName => {
      let ids = normalizeDashboardTabAssignment(nextParamAssignments[paramName]).filter(id => id !== activeTabId);
      if (!ids.length) {
        ids = [fallbackTabId];
      }
      nextParamAssignments[paramName] = ids;
    });

    const nextLayouts = { ...(dashboard.options?.tabLayouts || {}) };
    const deletedLayout = nextLayouts[activeTabId] || {};
    delete nextLayouts[activeTabId];
    nextLayouts[fallbackTabId] = {
      ...(nextLayouts[fallbackTabId] || {}),
      ...deletedLayout,
    };

    setActiveTabId(fallbackTabId);
    location.setSearch({ tab: fallbackTabId }, true);
    updateDashboard({
      options: {
        ...(dashboard.options || {}),
        tabs: remaining.map((tab, index) => ({ ...tab, order: index })),
        activeTabIdDefault: fallbackTabId,
        widgetTabAssignments: nextWidgetAssignments,
        parameterTabAssignments: nextParamAssignments,
        tabLayouts: nextLayouts,
      },
    });
  };

  const moveActiveTab = useCallback(
    direction => {
      if (!hasTabbedLayout || tabs.length <= 1) {
        return;
      }
      const orderedIds = tabs.map(t => t.id);
      const idx = orderedIds.indexOf(activeTabId);
      if (idx < 0) {
        return;
      }
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= orderedIds.length) {
        return;
      }
      const reordered = [...orderedIds];
      [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];

      const rawList = dashboard.options?.tabs || [];
      const byId = keyBy(rawList, t => String(t.id));
      const nextTabs = reordered.map((tabId, i) => {
        const key = String(tabId);
        const raw = byId[key];
        const def = find(tabs, t => t.id === tabId);
        if (raw) {
          return { ...raw, order: i };
        }
        return { id: tabId, name: def?.name || tabId, order: i };
      });

      updateDashboard({
        options: {
          ...(dashboard.options || {}),
          tabs: nextTabs,
          activeTabIdDefault: nextTabs[0].id,
        },
      });
    },
    [hasTabbedLayout, tabs, activeTabId, dashboard.options, updateDashboard]
  );

  const updateParameterBars = useCallback(
    updater => {
      const nextBars = typeof updater === "function" ? updater(parameterBars) : updater;
      updateDashboard({ options: { ...(dashboard.options || {}), parameterBars: nextBars } });
    },
    [parameterBars, updateDashboard]
  );

  const debouncedSaveParameterBars = useMemo(
    () =>
      debounce(nextBars => {
        updateDashboard({ options: { ...(dashboard.options || {}), parameterBars: nextBars } });
      }, 600),
    [dashboard.options, updateDashboard]
  );
  const debouncedSaveTabLayout = useMemo(
    () =>
      debounce(nextTabLayout => {
        const previousLayouts = dashboard.options?.tabLayouts || {};
        updateDashboard({
          options: {
            ...(dashboard.options || {}),
            tabLayouts: {
              ...previousLayouts,
              [activeTabId]: nextTabLayout,
            },
          },
        });
      }, 500),
    [dashboard.options, updateDashboard, activeTabId]
  );

  useEffect(
    () => () => {
      debouncedSaveParameterBars.cancel();
      debouncedSaveTabLayout.cancel();
    },
    [debouncedSaveParameterBars, debouncedSaveTabLayout]
  );

  const addGlobalParametersBar = () => {
    const nextRow = parameterBars.reduce((maxRow, bar) => Math.max(maxRow, bar.row), 0) + 2;
    const id = `bar-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;
    updateParameterBars([...parameterBars, { id, row: nextRow }]);
  };

  const removeGlobalParametersBar = barId => {
    if (!window.confirm("Remove this parameters bar?")) {
      return;
    }

    const remainingBars = parameterBars.filter(bar => bar.id !== barId);
    if (isEmpty(remainingBars)) {
      return;
    }

    const fallbackBarId = remainingBars[0].id;
    const nextAssignments = { ...parameterAssignments };
    const movedParamNames = [];
    Object.entries(nextAssignments).forEach(([paramName, assignedBarId]) => {
      if (assignedBarId === barId) {
        nextAssignments[paramName] = fallbackBarId;
        movedParamNames.push(paramName);
      }
    });

    const nextOrders = { ...parameterOrders };
    delete nextOrders[barId];
    if (!nextOrders[fallbackBarId]) {
      nextOrders[fallbackBarId] = [];
    }
    nextOrders[fallbackBarId] = [...nextOrders[fallbackBarId], ...movedParamNames];

    updateDashboard({
      options: {
        ...(dashboard.options || {}),
        parameterBars: remainingBars,
        parameterBarAssignments: nextAssignments,
        parameterBarOrders: nextOrders,
      },
    });
  };

  const onParameterBarsLayoutChange = positions => {
    const nextBars = parameterBars.map(bar => ({
      ...bar,
      row: positions[bar.id]?.row ?? bar.row,
    }));
    debouncedSaveParameterBars(nextBars);
  };

  const onMoveGlobalParameter = ({ paramName, sourceGroupId, targetGroupId, targetIndex }) => {
    if (!paramName || !sourceGroupId || !targetGroupId) {
      return;
    }

    const nextAssignments = { ...parameterAssignments, [paramName]: targetGroupId };
    const nextOrders = { ...parameterOrders };

    const sourceOrder = [...(nextOrders[sourceGroupId] || [])].filter(name => name !== paramName);
    nextOrders[sourceGroupId] = sourceOrder;

    const targetOrder = [...(nextOrders[targetGroupId] || [])].filter(name => name !== paramName);
    const safeTargetIndex = Math.max(0, Math.min(targetIndex, targetOrder.length));
    targetOrder.splice(safeTargetIndex, 0, paramName);
    nextOrders[targetGroupId] = targetOrder;

    updateDashboard({
      options: {
        ...(dashboard.options || {}),
        parameterBarAssignments: nextAssignments,
        parameterBarOrders: nextOrders,
      },
    });
  };

  const onBarParametersEdit = (barId, parameters) => {
    updateDashboard({
      options: {
        ...(dashboard.options || {}),
        parameterBarOrders: {
          ...parameterOrders,
          [barId]: map(parameters, "name"),
        },
      },
    });
  };

  useEffect(() => {
    if (pageContainer) {
      const unobserve = resizeObserver(pageContainer, () => {
        if (editingLayout) {
          const style = window.getComputedStyle(pageContainer, null);
          const bounds = pageContainer.getBoundingClientRect();
          const paddingLeft = parseFloat(style.paddingLeft) || 0;
          const paddingRight = parseFloat(style.paddingRight) || 0;
          setBottomPanelStyles({
            left: Math.round(bounds.left) + paddingRight,
            width: pageContainer.clientWidth - paddingLeft - paddingRight,
          });
        }

        // reflow grid when container changes its size
        window.dispatchEvent(new Event("resize"));
      });
      return unobserve;
    }
  }, [pageContainer, editingLayout]);

  const onDashboardLayoutChange = positions => {
    if (!editingLayout) {
      return;
    }
    if (!hasTabbedLayout) {
      saveDashboardLayout(positions);
      return;
    }

    const currentWidgetsById = keyBy(filteredWidgets, widget => String(widget.id));
    const nextTabLayout = Object.keys(currentWidgetsById).reduce((acc, widgetId) => {
      const widget = currentWidgetsById[widgetId];
      const base = widget.options.position;
      const next = positions[widgetId] || activeTabLayout[widgetId] || {};
      acc[widgetId] = {
        ...base,
        ...next,
      };
      return acc;
    }, {});

    debouncedSaveTabLayout(nextTabLayout);
  };

  return (
    <div className="container" ref={setPageContainer} data-test={`DashboardId${dashboard.id}Container`}>
      <DashboardHeader
        dashboardConfiguration={dashboardConfiguration}
        headerExtra={
          <DynamicComponent
            name="Dashboard.HeaderExtra"
            dashboard={dashboard}
            dashboardConfiguration={dashboardConfiguration}
          />
        }
      />
      {!isEmpty(filters) && (
        <div className="m-b-10 p-15 bg-white tiled" data-test="DashboardFilters">
          <Filters filters={filters} onChange={setFilters} />
        </div>
      )}
      {editingLayout && <DashboardSettings dashboardConfiguration={dashboardConfiguration} />}
      {(hasTabbedLayout || editingLayout) && (
        <div className="m-b-10 p-15 bg-white tiled" data-test="DashboardTabs">
          <Tabs
            activeKey={activeTabId}
            onChange={onTabChange}
            animated={false}
            tabBarExtraContent={
              editingLayout ? (
                <div className="dashboard-tabs-actions">
                  <Button size="small" onClick={addTab} data-test="AddDashboardTabButton">
                    Add Tab
                  </Button>
                  {hasTabbedLayout && (
                    <>
                      <Button size="small" onClick={renameActiveTab} data-test="RenameDashboardTabButton">
                        Rename
                      </Button>
                      <Button
                        size="small"
                        onClick={() => moveActiveTab(-1)}
                        disabled={!hasTabbedLayout || tabs.length <= 1 || activeTabIndex <= 0}
                        title="Move tab left (toward start)"
                        aria-label="Move tab left"
                        data-test="MoveDashboardTabLeftButton">
                        <i className="zmdi zmdi-chevron-left" aria-hidden />
                      </Button>
                      <Button
                        size="small"
                        onClick={() => moveActiveTab(1)}
                        disabled={
                          !hasTabbedLayout ||
                          tabs.length <= 1 ||
                          activeTabIndex < 0 ||
                          activeTabIndex >= tabs.length - 1
                        }
                        title="Move tab right"
                        aria-label="Move tab right"
                        data-test="MoveDashboardTabRightButton">
                        <i className="zmdi zmdi-chevron-right" aria-hidden />
                      </Button>
                      <Button
                        size="small"
                        danger
                        disabled={tabs.length <= 1}
                        onClick={removeActiveTab}
                        data-test="RemoveDashboardTabButton">
                        Remove
                      </Button>
                    </>
                  )}
                </div>
              ) : null
            }>
            {tabs.map(tab => (
              <TabPane tab={tab.name} key={tab.id} />
            ))}
          </Tabs>
        </div>
      )}
      {editingLayout && !isEmpty(tabParameters) && (
        <div className="m-b-10 p-15 bg-white tiled">
          <Button type="dashed" onClick={addGlobalParametersBar} data-test="AddGlobalParametersBarButton">
            Add Global Parameters Bar
          </Button>
        </div>
      )}
      <div id="dashboard-container">
        <DashboardGrid
          dashboard={dashboard}
          widgets={filteredWidgets}
          globalParameters={tabParameters}
          parameterBars={parameterBars}
          parameterAssignments={parameterAssignments}
          parameterOrders={parameterOrders}
          layoutOverrides={layoutOverrides}
          filters={filters}
          isEditing={editingLayout}
          onLayoutChange={onDashboardLayoutChange}
          onParameterBarsLayoutChange={editingLayout ? onParameterBarsLayoutChange : () => {}}
          onRemoveParameterBar={editingLayout ? removeGlobalParametersBar : () => {}}
          onMoveGlobalParameter={editingLayout ? onMoveGlobalParameter : () => {}}
          onBarParametersEdit={editingLayout ? onBarParametersEdit : () => {}}
          widgetTabAssignmentOptions={widgetTabAssignmentOptions}
          parameterTabAssignmentOptions={parameterTabAssignmentOptions}
          onGlobalParametersEdit={editingLayout ? onParametersEdit : null}
          onGlobalParametersValuesChange={refreshDashboard}
          onBreakpointChange={setGridDisabled}
          onLoadWidget={loadWidget}
          onRefreshWidget={refreshWidget}
          onRemoveWidget={removeWidget}
          onParameterMappingsChange={loadDashboard}
        />
      </div>
      {editingLayout && (
        <AddWidgetContainer dashboardConfiguration={dashboardConfiguration} style={bottomPanelStyles} />
      )}
    </div>
  );
}

DashboardComponent.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function DashboardPage({ dashboardSlug, dashboardId, onError }) {
  const [dashboard, setDashboard] = useState(null);
  const handleError = useImmutableCallback(onError);

  useEffect(() => {
    Dashboard.get({ id: dashboardId, slug: dashboardSlug })
      .then(dashboardData => {
        recordEvent("view", "dashboard", dashboardData.id);
        setDashboard(dashboardData);

        // if loaded by slug, update location url to use the id
        if (!dashboardId) {
          location.setPath(url.parse(dashboardData.url).pathname, true);
        }
      })
      .catch(handleError);
  }, [dashboardId, dashboardSlug, handleError]);

  return <div className="dashboard-page">{dashboard && <DashboardComponent dashboard={dashboard} />}</div>;
}

DashboardPage.propTypes = {
  dashboardSlug: PropTypes.string,
  dashboardId: PropTypes.string,
  onError: PropTypes.func,
};

DashboardPage.defaultProps = {
  dashboardSlug: null,
  dashboardId: null,
  onError: PropTypes.func,
};

// route kept for backward compatibility
routes.register(
  "Dashboards.LegacyViewOrEdit",
  routeWithUserSession({
    path: "/dashboard/:dashboardSlug",
    render: pageProps => <DashboardPage {...pageProps} />,
  })
);

routes.register(
  "Dashboards.ViewOrEdit",
  routeWithUserSession({
    path: "/dashboards/:dashboardId([^-]+)(-.*)?",
    render: pageProps => <DashboardPage {...pageProps} />,
  })
);
